<template>
  <!-- Ex-Serving Eligibility Case -->
  <div id="ex-serving">
    <div class="form-section">
      <VeteranInfo
        v-if="formResponse && formResponse.veteranInfo"
        :subFormData="formResponse.veteranInfo"
        :referenceData="referenceData"
        :serviceAgencyLabel="formLabels.veteranInfo.serviceAgencyLabel"
        :serviceTypeLabel="formLabels.veteranInfo.serviceTypeLabel"
        :noEligibilityChange="noEligibilityChange"
        :disableEligibilityFields="disableEligibilityFields"
        :initialData="initialData"
        :serviceConnection="serviceConnection"
        :defaultVeteranInfo="defaultSubFormTemplate.veteranInfo"
        :isChangeCover="isChangeCover"
        @updated="updateVeteranInfo"
      />
    </div>

    <!-- Date of Enlistment Input Field -->
    <FormField overrideClass="multi-input-row form-section">
      <div>
        <FormLabel labelText="Date of enlistment" for="enlistment-date" required />
        <template v-if="disableEligibilityFields">
          <p class="disabled-input date-input" id="enlistment-date">{{ returnWhiteSpaceIfEmptyStr(formResponse.enlistmentDate) }}</p>
        </template>
        <template v-else>
          <FormInput
            id="enlistment-date"
            placeholder="DD / MM / YYYY"
            v-maska="'## / ## / ####'"
            type="text"
            :isDateInput="true"
            v-model.lazy="v$.formResponse.enlistmentDate.$model"
            :overrideClass="v$.formResponse.enlistmentDate.$error ? 'error' : null"
          />
        </template>
        <label v-if="v$.formResponse.enlistmentDate.$error" for="enlistment-date" class="error">{{ dateOfEnlistmentError }}</label>
      </div>

      <!-- Date of Discharge Input Field -->
      <!-- TODO: add correct date validator -->
      <div>
        <FormLabel labelText="Date of discharge" for="discharge-date" required />
        <template v-if="disableEligibilityFields">
          <p class="disabled-input date-input" id="discharge-date">{{ returnWhiteSpaceIfEmptyStr(formResponse.dischargeDate) }}</p>
        </template>
        <template v-else>
          <FormInput
            id="discharge-date"
            placeholder="DD / MM / YYYY"
            v-maska="'## / ## / ####'"
            type="text"
            :isDateInput="true"
            v-model.lazy="v$.formResponse.dischargeDate.$model"
            :overrideClass="v$.formResponse.dischargeDate.$error ? 'error' : null"
          />
        </template>
        <label v-if="v$.formResponse.dischargeDate.$error" for="discharge-date" class="error">{{ dateOfDischargeError }}</label>
      </div>
    </FormField>
  </div>
</template>

<script>
import { required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { DATE_VALIDATOR } from '@general/js/validators';
import { FormField, FormLabel, FormInput } from '@/components';
import { Eligibility } from '@general/js/enums';
import { mapMutations } from 'vuex';
import { beforeYear1970, getServiceAgencyLabel, getServicePeriodLabel, isDateBefore, isInvalid, isPastDateExcludingToday, lessThan90Days, overPastDate } from '@general/js/utils';
import { returnWhiteSpaceIfEmptyStr } from '@/utils';
import { VeteranInfo } from './components';

export default {
  setup() { return { v$: useVuelidate() }; },
  name: 'ExServing',

  data() {
    return {
      formLabels: {
        veteranInfo: {
          serviceAgencyLabel: '',
          serviceTypeLabel: '',
        },
      },
      formResponse: {
        enlistmentDate: '',
        dischargeDate: '',
      },
      eligibility: Eligibility.EX_SERVING,
      veteranInfoIsInvalid: true,
    };
  },
  components: {
    FormField,
    FormLabel,
    FormInput,
    VeteranInfo,
  },

  props: {
    subFormData: {
      type: Object,
      required: true,
    },
    referenceData: {
      type: Object,
      required: true,
    },
    noEligibilityChange: {
      type: Boolean,
    },
    disableEligibilityFields: {
      type: Boolean,
    },
    initialData: {
      type: Object,
    },
    serviceConnection: {
      type: String,
    },
    defaultSubFormTemplate: {
      type: Object,
    },
    isChangeCover: {
      type: Boolean,
    },
  },

  watch: {
    formResponse: {
      handler() {
        if (this.formResponse) {
          this.$emit('updated', { subFormData: this.formResponse, isInvalid: this.v$.$invalid || this.v$.$error });
        }
      },
      deep: true,
    },
    // Clears and restores prefill data when service connection changes (Self, Partner)
    serviceConnection(newValue) {
      if (this.isChangeCover && this.noEligibilityChange && this.initialData.serviceConnection !== '') {
        if (newValue !== this.initialData.serviceConnection) {
          this.formResponse = JSON.parse(JSON.stringify(this.defaultSubFormTemplate));
        } else {
          this.formResponse = JSON.parse(JSON.stringify(this.initialData.subFormData));
        }
      }
    },
  },

  computed: {
    dateOfEnlistmentError() {
      if (this.v$.formResponse.enlistmentDate.$error && this.v$.formResponse.enlistmentDate.DATE_VALIDATOR.$invalid) {
        return 'Please enter the enlistment date using the format DD/MM/YYYY.';
      }

      if (this.v$.formResponse.enlistmentDate.$error && this.v$.formResponse.enlistmentDate.overPastDate.$invalid) {
        return 'Enlistment date cannot be more than 109 years ago.';
      }

      if (this.v$.formResponse.enlistmentDate.$error && this.v$.formResponse.enlistmentDate.isPastDateExcludingToday.$invalid) {
        return 'Enlistment date must be a past date.';
      }

      if (this.v$.formResponse.enlistmentDate.$error && this.v$.formResponse.enlistmentDate.required.$invalid) {
        return 'Please include a response for date of enlistment.';
      }

      return '';
    },

    dateOfDischargeError() {
      if (this.v$.formResponse.dischargeDate.$error && this.v$.formResponse.dischargeDate.DATE_VALIDATOR.$invalid) {
        return 'Please enter the discharge date using the format DD/MM/YYYY.';
      }

      if (this.v$.formResponse.dischargeDate.$error && this.v$.formResponse.dischargeDate.lessThan90Days.$invalid) {
        return 'We can only accept future dates within 90 days of today.';
      }

      if (this.v$.formResponse.dischargeDate.$error && this.v$.formResponse.dischargeDate.beforeYear1970.$invalid) {
        return 'Discharge date cannot be earlier than 1970.';
      }

      if (this.v$.formResponse.dischargeDate.$error && this.v$.formResponse.dischargeDate.beforeEnlistment.$invalid) {
        return 'Discharge date cannot be before enlistment date.';
      }

      if (this.v$.formResponse.dischargeDate.$error && this.v$.formResponse.dischargeDate.required.$invalid) {
        return 'Please include a response for date of discharge.';
      }

      return '';
    },
  },

  mounted() {
    this.formLabels.veteranInfo.serviceAgencyLabel = getServiceAgencyLabel(this.eligibility);
    this.formLabels.veteranInfo.serviceTypeLabel = getServicePeriodLabel(this.eligibility);
    this.formResponse = { ...this.subFormData };
  },

  validations: {
    formResponse: {
      enlistmentDate: {
        required,
        DATE_VALIDATOR,
        isPastDateExcludingToday,
        overPastDate,
      },
      dischargeDate: {
        required,
        DATE_VALIDATOR,
        lessThan90Days,
        beforeYear1970,
        beforeEnlistment(value) {
          return isDateBefore(value, this.formResponse.enlistmentDate);
        },
      },
    },
    veteranInfoIsInvalid: {
      isInvalid,
    },
  },

  methods: {
    ...mapMutations(['updateEligibilityFormData']),

    validationStatus(validation) {
      // TODO: find out how to modularise this instead of using it multiple times across child components
      return {
        error: validation.$error,
        dirty: validation.$dirty,
      };
    },

    updateVeteranInfo(data) {
      this.formResponse.veteranInfo = JSON.parse(JSON.stringify(data.veteranInfo));
      this.veteranInfoIsInvalid = data.isInvalid;
    },

    returnWhiteSpaceIfEmptyStr,
  },
};
</script>

<style lang="scss" scoped>
#ex-serving {
  .date-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 25px;

    .form-field {
      @include desktop {
        margin-bottom: unset;
      }
      min-width: 166px;
      max-width: 40%;
      grid-template-columns: unset;
    }
  }
}
</style>
